/**
 * Settings → Branding: the house style every form inherits.
 *
 * One palette, one logo row, one typeface, one mode — and a form wears each of
 * them for every key its own theme does not answer. The point of the screen is
 * the thirty-forms case: a company with one logo sets it once here instead of
 * on every form, and replaces it once when it changes.
 *
 * Two things about it are unlike every other settings page:
 *
 * 1. **Saving reaches live forms immediately.** There is no draft and no
 *    publish at the account level, so the save asks first and the question
 *    names the number — "14 of 30 forms" — rather than saying "are you sure".
 * 2. **The logo row is not part of the save.** It moves with its bytes, the
 *    moment a file is picked or removed, because a brand pointing at a file
 *    nothing stored (or bytes nothing points at) is the one inconsistency
 *    worth spending a round trip to make impossible. The SaveBar says so.
 *
 * Plan gating follows the form exactly: palette, accent and background are
 * free on every plan; logos, typeface and mode are the `customBranding` half
 * and render disabled with an upsell. The server pins them regardless, so this
 * is presentation, not enforcement.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AccountBrand, BrandSwatch, FormLogo, GradientDir, LogoAlign } from '../../shared/types';
import {
  accentWash, GRADIENT_DIRS, GRADIENT_LABEL, gradientCss, LOGO_ALIGNS, parseGradient, themeLogos,
} from '../../shared/types';
import { BRAND_FONTS, fontStack, fontsHref } from '../../shared/brandFonts';
import {
  BrandOverrideForm, BrandingState, deleteBrandLogo, getBranding, getBrandOverrides,
  moveBrandLogo, resetFormsToBranding, saveBranding, uploadBrandLogo,
} from '../../api/admin';
import { Modal, SaveBar } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { PHONE, useMediaQuery } from '../../shared/media';

/** A swatch as the editor holds it — with a key, so a rename cannot reorder. */
interface Row extends BrandSwatch { rid: number }
let nextRid = 1;

const HEX = /^#[0-9a-f]{6}$/i;

/** A logo position, in the words the page uses for it. */
const ALIGN_LABEL: Record<string, string> = { left: 'Left', center: 'Middle', right: 'Right' };

/**
 * A stored background as the wells and the direction buttons hold it.
 *
 * The field used to be free text, and `brandBg` on the server still accepts
 * any gradient — a brand promoted from a form carries a stock preset's
 * `linear-gradient(135deg, #a 0%, #b 100%)`, and somebody may have typed a
 * `conic-gradient` here while it was a text box. `parseGradient` answers for
 * the shapes `gradientCss` writes and null for everything else, which is what
 * `custom: true` is: the page says the value is not one it wrote rather than
 * silently rewriting it on first render.
 */
function parseWash(v: string): { c1: string; c2: string; dir: GradientDir; custom: boolean } {
  const raw = (v || '').trim();
  const flat = { c2: '', dir: 'diagonal' as GradientDir, custom: false };
  if (!raw) return { c1: '', ...flat };
  if (HEX.test(raw)) return { c1: raw.toLowerCase(), ...flat };
  const g = parseGradient(raw);
  return g ? { ...g, custom: false } : { c1: '', ...flat, custom: true };
}

/** What a theme key is called in a sentence a person reads. */
const keyLabel = (k: string) => (({
  accent: 'accent colour', accent2: 'accent gradient', accentGradient: 'accent gradient',
  bg: 'background', font: 'typefaces', mode: 'colour mode',
  logos: 'logo', logo: 'logo', presetId: 'theme preset',
} as Record<string, string>)[k] || k);

export default function BrandingSettings() {
  const toast = useToast();
  const phone = useMediaQuery(PHONE);

  const [state, setState] = useState<BrandingState | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [accent, setAccent] = useState('');
  const [accent2, setAccent2] = useState('');
  const [accentDir, setAccentDir] = useState<GradientDir>('diagonal');
  const [bg, setBg] = useState('');
  /**
   * The second colour each gradient had last, so unticking the box and
   * ticking it again puts back the colour that was there rather than a
   * default the person then has to find again.
   */
  const [accent2Memo, setAccent2Memo] = useState('#764ba2');
  const [bg2Memo, setBg2Memo] = useState('#764ba2');
  /** Where the next upload goes, when more than one position is free. */
  const [pickAlign, setPickAlign] = useState<LogoAlign>('center');
  /** The logo a move is in flight for, so its row cannot be clicked twice. */
  const [movingLogo, setMovingLogo] = useState('');
  const [font, setFont] = useState<{ body?: string; heading?: string }>({});
  const [mode, setMode] = useState<'light' | 'dark' | 'auto' | ''>('');
  const [logos, setLogos] = useState<FormLogo[]>([]);

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  /**
   * The forms still setting their own look, and which of them are ticked.
   *
   * This is the whole point of the effort for a customer who has been setting
   * the same logo on every form since August: one list, and one button.
   */
  const [overrides, setOverrides] = useState<BrandOverrideForm[] | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [resetting, setResetting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getBranding()
      .then((r) => {
        setState(r);
        adopt(r.brand);
      })
      .catch(() => setError('Branding is not available right now.'));
    loadOverrides();
  }, []);

  function loadOverrides() {
    getBrandOverrides()
      .then((r) => { setOverrides(r.forms); setPicked(new Set(r.forms.map((f) => f.id))); })
      .catch(() => setOverrides([]));
  }

  async function onResetPicked() {
    setResetting(true);
    try {
      const r = await resetFormsToBranding([...picked]);
      toast(`${r.reset} form${r.reset === 1 ? '' : 's'} now use${r.reset === 1 ? 's' : ''} your account branding.`);
      loadOverrides();
      // The number in the save confirm moves with them.
      getBranding().then((g) => setState(g)).catch(() => undefined);
    } catch (e: any) { setError(e.message || 'Those forms could not be reset.'); }
    setResetting(false);
  }

  function adopt(brand: AccountBrand) {
    setRows((brand.palette || []).map((s) => ({ ...s, rid: nextRid++ })));
    setAccent(brand.accent || '');
    setAccent2(brand.accent2 || '');
    setAccentDir(brand.accentGradient || 'diagonal');
    if (brand.accent2) setAccent2Memo(brand.accent2);
    setBg(brand.bg || '');
    const parsed = parseWash(brand.bg || '');
    if (parsed.c2) setBg2Memo(parsed.c2);
    setFont(brand.font || {});
    setMode(brand.mode || '');
    setLogos(themeLogos(brand));
  }

  // The canvas of this page is the preview below, and it wants the real faces.
  useEffect(() => {
    const href = fontsHref(font);
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [font.body, font.heading]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Everything the save posts, and nothing else — the logo row moves on its
   * own, so it is deliberately absent: including it would make every upload
   * read as an unsaved change the guard then offers to throw away.
   */
  const snapshot = useMemo(
    () => ({ rows: rows.map(({ rid: _rid, ...s }) => s), accent, accent2, accentDir, bg, font, mode }),
    [rows, accent, accent2, accentDir, bg, font, mode]
  );
  const { dirty, markSaved } = useDirty(snapshot, !!state);
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: 'Your changes to the account branding',
    onSave: () => save(),
  });

  const off = !!state && !state.customBranding;
  const dim = off ? { opacity: 0.5, pointerEvents: 'none' as const } : undefined;
  const free = LOGO_ALIGNS.filter((a) => !logos.some((l) => l.align === a));
  /** The chosen upload position, kept inside what is actually free. */
  const nextAlign: LogoAlign = free.includes(pickAlign) ? pickAlign : (free[0] || 'center');
  /** The background as the two wells hold it. */
  const bgWash = useMemo(() => parseWash(bg), [bg]);

  // ---- the palette ----
  const setRow = (rid: number, patch: Partial<BrandSwatch>) =>
    setRows((list) => list.map((r) => (r.rid === rid ? { ...r, ...patch } : r)));
  const addRow = () =>
    setRows((list) => [...list, { rid: nextRid++, name: '', hex: '#667eea' }]);
  const removeRow = (rid: number) => setRows((list) => list.filter((r) => r.rid !== rid));
  /**
   * Move a swatch with buttons rather than a drag.
   *
   * Order is only the order the pickers offer them in, so a drag would be
   * ceremony — and every drag has to carry a tap equivalent anyway. Two
   * buttons ARE the tap equivalent, on every pointer, so there is one path
   * instead of two that can disagree.
   */
  const move = (rid: number, by: -1 | 1) =>
    setRows((list) => {
      const i = list.findIndex((r) => r.rid === rid);
      const j = i + by;
      if (i < 0 || j < 0 || j >= list.length) return list;
      const next = [...list];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  /**
   * Swatches the server would actually keep, and why the others go.
   *
   * Two rules, and the second one is the one that surprises people: a swatch
   * needs a name, AND the palette holds one row per COLOUR — the same hex
   * named twice cannot be picked between, so the sanitizer keeps the first.
   * Adding two swatches and naming them before recolouring the second is the
   * obvious way to use this screen, and it silently lost one until the screen
   * said so here. This mirrors `sanitizeAccountBrand`; if that rule changes,
   * this changes with it.
   */
  const usable = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((r) => {
      const hex = r.hex.toLowerCase();
      if (!r.name.trim() || !HEX.test(hex) || seen.has(hex)) return false;
      seen.add(hex);
      return true;
    });
  }, [rows]);
  const unnamed = rows.filter((r) => !r.name.trim() || !HEX.test(r.hex)).length;
  const duplicate = rows.length - usable.length - unnamed;
  /** The hexes that appear more than once, so their rows can say so. */
  const dupeHexes = useMemo(() => {
    const count = new Map<string, number>();
    for (const r of rows) count.set(r.hex.toLowerCase(), (count.get(r.hex.toLowerCase()) || 0) + 1);
    return new Set([...count].filter(([, n]) => n > 1).map(([h]) => h));
  }, [rows]);

  // ---- the logo row (saves itself) ----
  async function pickLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const r = await uploadBrandLogo(file, nextAlign);
      setLogos(r.logos);
      setError('');
      toast(`Logo added on the ${ALIGN_LABEL[r.logo.align].toLowerCase()} — every form that inherits it shows it now.`);
    } catch (err: any) { setError(err.message || 'The logo could not be uploaded.'); }
  }

  /**
   * Move a logo to another position.
   *
   * Its own round trip, because the logo row is not part of the save (see the
   * file header): a position that only moved when Save was pressed would be
   * the one half of the row that could get out of step with its bytes.
   */
  async function moveLogo(url: string, align: LogoAlign) {
    setMovingLogo(url);
    try {
      const r = await moveBrandLogo(url, align);
      setLogos(r.logos);
      setError('');
      toast(`Logo moved to the ${ALIGN_LABEL[align].toLowerCase()}.`);
    } catch (err: any) { setError(err.message || 'The logo could not be moved.'); }
    setMovingLogo('');
  }

  async function removeLogo(url: string) {
    try {
      const r = await deleteBrandLogo(url);
      setLogos(r.logos);
      setError('');
      toast('Logo removed.');
    } catch (err: any) { setError(err.message || 'The logo could not be removed.'); }
  }

  // ---- the save ----
  function onSaveClick() {
    // The confirm exists to stop a silent account-wide repaint, so a brand
    // nothing inherits does not need one.
    if (state && state.inheritingCount > 0) { setConfirming(true); return; }
    void save().catch(() => undefined);
  }

  async function save() {
    setSaving(true); setError('');
    try {
      const brand: AccountBrand = {
        ...(usable.length ? { palette: usable.map(({ rid: _rid, ...s }) => s) } : {}),
        ...(accent ? { accent } : {}),
        // A second stop with no first one is a gradient with one end, and the
        // server drops it — so the page does not send one either. Diagonal is
        // what an absent direction means, and is stored as absence.
        ...(accent && accent2 ? { accent2 } : {}),
        ...(accent && accent2 && accentDir !== 'diagonal' ? { accentGradient: accentDir } : {}),
        ...(bg ? { bg } : {}),
        ...(font.body || font.heading ? { font } : {}),
        ...(mode ? { mode } : {}),
      };
      const r = await saveBranding(brand);
      adopt(r.brand);
      markSaved();
      setConfirming(false);
      toast(
        state && state.inheritingCount > 0
          ? `Branding saved — ${state.inheritingCount} form${state.inheritingCount === 1 ? '' : 's'} updated.`
          : 'Branding saved.'
      );
    } catch (err: any) {
      setError(err.message || 'The branding could not be saved.');
      setConfirming(false);
      throw err; // so "Save and leave" stays put on a failure
    } finally {
      setSaving(false);
    }
  }

  if (!state) {
    return <div className="fs-card pad st-pcard"><p style={{ margin: 0, color: 'var(--text-muted)' }}>{error || 'Loading…'}</p></div>;
  }

  /**
   * A colour that may fade into a second one — the shape both defaults now
   * have.
   *
   * The background used to be a text box that said "or a gradient" and left
   * the person to write the CSS by hand, and the accent could not be a
   * gradient at all although a form's header band and its submit button have
   * always been drawn as `accent -> accent2` (see `accentWash`). One control
   * for both, so the two fields answer the same question the same way.
   */
  const washField = (o: {
    id: string; label: string; hint: string;
    /** The first stop, '' while the brand answers nothing for this key. */
    c1: string;
    /** The second stop, '' for a flat colour. */
    c2: string;
    /** Which way the pair runs. Only shown while there is a pair. */
    dir: GradientDir;
    /** What the well shows while `c1` is empty — never written until touched. */
    fallback: string;
    /** The colour the box puts back when it is ticked again. */
    memo: string;
    /** True when what is stored is not a shape these wells can write. */
    custom?: boolean;
    set: (c1: string, c2: string, dir: GradientDir) => void;
  }) => {
    const base = HEX.test(o.c1) ? o.c1 : o.fallback;
    return (
      <div className="fs-field">
        <label htmlFor={o.id}>{o.label}</label>
        <div className="br-colour-row">
          <input id={o.id} type="color" value={base}
            aria-label={o.label}
            onChange={(e) => o.set(e.target.value, o.c2, o.dir)} />
          <label className="br-check">
            <input type="checkbox" checked={!!o.c2}
              onChange={(e) => o.set(base, e.target.checked ? (o.memo || '#764ba2') : '', o.dir)} />
            Gradient
          </label>
          {!!o.c2 && (
            <input type="color" value={HEX.test(o.c2) ? o.c2 : '#764ba2'}
              aria-label={`${o.label} — second colour`}
              onChange={(e) => o.set(base, e.target.value, o.dir)} />
          )}
          <button className="fs-btn neutral sm" type="button"
            onClick={() => o.set('', '', 'diagonal')} disabled={!o.c1 && !o.c2}>
            Clear
          </button>
        </div>
        {/* Which way it runs, on its own line: four buttons beside two wells
            and a box is a scroll at 375, and the row only exists at all once
            there is a second colour to run to. */}
        {!!o.c2 && (
          <div className="br-segs br-dirs" role="group" aria-label={`${o.label} — gradient direction`}>
            {GRADIENT_DIRS.map((g) => (
              <button key={g} type="button" aria-pressed={o.dir === g}
                className={`fs-btn ${o.dir === g ? 'primary' : 'neutral'} sm`}
                onClick={() => o.set(base, o.c2, g)}>{GRADIENT_LABEL[g]}</button>
            ))}
          </div>
        )}
        <span className="hint">{o.hint}</span>
        {o.custom && (
          <span className="hint br-warn">
            This is a custom CSS value. Touching a colour above replaces it.
          </span>
        )}
      </div>
    );
  };

  const swatchRow = (r: Row, i: number) => (
    <div className={`br-swatch${dupeHexes.has(r.hex.toLowerCase()) && !usable.includes(r) ? ' dupe' : ''}`} key={r.rid}>
      <input
        type="color" aria-label={`Colour for ${r.name || 'this swatch'}`}
        value={HEX.test(r.hex) ? r.hex : '#667eea'}
        onChange={(e) => setRow(r.rid, { hex: e.target.value })}
        title={dupeHexes.has(r.hex.toLowerCase()) ? 'Two swatches share this colour' : undefined}
      />
      <input
        className="fs-input" placeholder="Name — Bright Burgundy" value={r.name}
        onChange={(e) => setRow(r.rid, { name: e.target.value })}
      />
      <input
        className="fs-input br-group" placeholder="Group (optional)" value={r.group || ''}
        onChange={(e) => setRow(r.rid, { group: e.target.value || undefined })}
      />
      <div className="br-swatch-acts">
        <button className="fs-btn icon" type="button" aria-label="Move up"
          disabled={i === 0} onClick={() => move(r.rid, -1)}>↑</button>
        <button className="fs-btn icon" type="button" aria-label="Move down"
          disabled={i === rows.length - 1} onClick={() => move(r.rid, 1)}>↓</button>
        <button className="fs-btn icon" type="button" aria-label={`Remove ${r.name || 'this swatch'}`}
          onClick={() => removeRow(r.rid)}>✕</button>
      </div>
    </div>
  );

  return (
    <div className="st-col st-branding">
      <div className="fs-page-head">
        <div className="text">
          <h1>Branding</h1>
          <div className="fs-page-sub br-lede">
            The house style every form starts with. Every form uses it for anything the form does
            not set itself, so a logo replaced here is replaced on all of them.
          </div>
        </div>
      </div>
      {error && <div className="br-error">{error}</div>}

      {/* ---- the palette: free on every plan ---- */}
      <div className="fs-card pad st-pcard">
        <h3 className="br-h">Colours</h3>
        <p className="br-sub">
          The colours this company is allowed to be. Forms pick their accent from this list,
          so a form can be on-brand without being identical.
        </p>
        {rows.length > 0 && <div className="br-swatches">{rows.map(swatchRow)}</div>}
        <button className="fs-btn neutral sm" type="button" onClick={addRow} style={{ marginTop: rows.length ? 10 : 0 }}>
          Add a colour
        </button>
        {(unnamed > 0 || duplicate > 0) && (
          <div className="br-hint br-warn">
            {[
              unnamed > 0 && `${unnamed} needs a name`,
              duplicate > 0 && `${duplicate} repeats a colour already in the list`,
            ].filter(Boolean).join(', and ')} — {unnamed + duplicate === 1 ? 'it will' : 'they will'} not be saved.
          </div>
        )}

        <div className="br-two" style={{ marginTop: 18 }}>
          {washField({
            id: 'br-accent', label: 'Default accent',
            hint: 'Used by a form that sets no accent of its own. A second colour blends across the submit button, and across the page behind the form when no background is set — everything else takes the first colour.',
            c1: accent, c2: accent2, dir: accentDir, fallback: '#667eea', memo: accent2Memo,
            set: (c1, c2, dir) => {
              setAccent(c1);
              setAccent2(c1 ? c2 : '');
              setAccentDir(c1 && c2 ? dir : 'diagonal');
              if (c2) setAccent2Memo(c2);
            },
          })}
          {washField({
            id: 'br-bg', label: 'Default background',
            hint: 'The area around the form card, for a form that sets no background of its own.',
            c1: bgWash.c1, c2: bgWash.c2, dir: bgWash.dir, fallback: '#f7fafc', memo: bg2Memo,
            custom: bgWash.custom,
            set: (c1, c2, dir) => {
              setBg(c1 ? (c2 ? gradientCss(c1, c2, dir) : c1) : '');
              if (c2) setBg2Memo(c2);
            },
          })}
        </div>
      </div>

      {/* ---- the customBranding half ---- */}
      <div className="fs-card pad st-pcard">
        <h3 className="br-h">
          Logo, type and mode
          {off && <span className="fs-pill neutral" style={{ marginLeft: 8 }}>paid plans</span>}
        </h3>
        {off && (
          <div className="br-hint" style={{ marginBottom: 12 }}>
            A shared logo, typeface and dark mode need a plan with custom branding — the colours
            above stay yours on every plan.
          </div>
        )}

        <div style={dim}>
          <label className="br-label">{logos.length > 1 ? 'Logos' : 'Logo'}</label>
          <p className="br-sub">
            Up to three — one on the left, one in the middle, one on the right. Pick where each one
            goes below; you can move it afterwards. PNG, JPEG, GIF or WebP, up to 2 MB.
          </p>
          {logos.map((l) => (
            <div className="br-logo" key={l.url}>
              <img src={l.url} alt="" />
              {/* Two logos cannot share a position — one would print on top of
                  the other — so a taken one is shown taken rather than
                  accepted and then refused by the server. Same rule, same
                  wording, as the builder's own row. */}
              <div className="br-segs br-logo-pos">
                {LOGO_ALIGNS.map((a) => {
                  const taken = logos.some((o) => o.url !== l.url && o.align === a);
                  return (
                    <button key={a} type="button" aria-pressed={l.align === a}
                      className={`fs-btn ${l.align === a ? 'primary' : 'neutral'} sm`}
                      disabled={taken || !!movingLogo}
                      title={taken ? `Another logo is already on the ${ALIGN_LABEL[a].toLowerCase()}. Move that one first.` : undefined}
                      onClick={() => { if (l.align !== a) void moveLogo(l.url, a); }}>
                      {ALIGN_LABEL[a]}
                    </button>
                  );
                })}
              </div>
              <span style={{ flex: 1 }} />
              <button className="fs-btn danger sm" type="button" disabled={!!movingLogo}
                onClick={() => removeLogo(l.url)}>Remove</button>
            </div>
          ))}
          {free.length > 0 && (
            <>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }} onChange={pickLogo} />
              <div className="br-upload-row">
                <button className="fs-btn neutral sm" type="button" onClick={() => fileRef.current?.click()}>
                  {logos.length ? 'Add another logo' : 'Upload a logo'}
                </button>
                {/* Where it lands, asked BEFORE the file picker — one free
                    position is not a question, so it is answered in words. */}
                {free.length > 1 ? (
                  <label className="br-upload-pos">
                    <span>Position</span>
                    <select className="fs-input" value={nextAlign}
                      onChange={(e) => setPickAlign(e.target.value as LogoAlign)}>
                      {free.map((a) => <option key={a} value={a}>{ALIGN_LABEL[a]}</option>)}
                    </select>
                  </label>
                ) : (
                  <span className="hint">It goes on the {ALIGN_LABEL[nextAlign].toLowerCase()} — the last position free.</span>
                )}
              </div>
            </>
          )}
          <div className="br-hint">Logos save as soon as you pick one, or move one — they carry their own file.</div>

          <div className="br-two" style={{ marginTop: 18 }}>
            <div className="fs-field">
              <label htmlFor="br-heading">Heading typeface</label>
              <select id="br-heading" className="fs-input" value={font.heading || ''}
                onChange={(e) => setFont((f) => ({ ...f, heading: e.target.value || undefined }))}>
                {BRAND_FONTS.map((f) => <option key={f.family || 'sys'} value={f.family}>{f.label}</option>)}
              </select>
            </div>
            <div className="fs-field">
              <label htmlFor="br-body">Body typeface</label>
              <select id="br-body" className="fs-input" value={font.body || ''}
                onChange={(e) => setFont((f) => ({ ...f, body: e.target.value || undefined }))}>
                {BRAND_FONTS.map((f) => <option key={f.family || 'sys'} value={f.family}>{f.label}</option>)}
              </select>
            </div>
          </div>

          <div className="fs-field" style={{ marginTop: 14 }}>
            <label>Default mode</label>
            <div className="br-segs">
              {([['', 'Not set'], ['light', 'Light'], ['dark', 'Dark'], ['auto', 'Follow the visitor']] as const)
                .map(([v, label]) => (
                  <button key={v || 'unset'} type="button"
                    className={`fs-btn ${mode === v ? 'primary' : 'neutral'} sm`}
                    onClick={() => setMode(v)}>{label}</button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* ---- what a form inherits, drawn ---- */}
      <div className="fs-card pad st-pcard">
        <h3 className="br-h">What a form inherits</h3>
        <p className="br-sub">A form that sets nothing of its own looks like this.</p>
        <div className="br-preview" style={{ background: bg || undefined }}>
          <div className="br-preview-card" style={{
            ...(fontStack(font.body) ? { fontFamily: fontStack(font.body)! } : {}),
          }}>
            {logos.length > 0 && (
              // Positioned, like the form's own row, so the preview answers
              // "where does this one land" as well as "which ones are there".
              <div className="br-preview-logos">
                {logos.map((l) => (
                  <div key={l.url} className={`of-logo-${l.align}`}><img src={l.url} alt="" /></div>
                ))}
              </div>
            )}
            <h4 style={{ ...(fontStack(font.heading || font.body) ? { fontFamily: fontStack(font.heading || font.body)! } : {}) }}>
              Your form’s heading
            </h4>
            <p>A question would sit here, in the body face.</p>
            {/* The band a respondent gets: accent -> accent2, flat when there
                is only one (the same expression `.of-btn` draws). */}
            <span className="br-preview-btn"
              style={{ background: accentWash({ accent, accent2, accentGradient: accentDir }) }}>Submit</span>
          </div>
        </div>
      </div>

      {/* ---- what is still doing its own thing (B5) ---- */}
      {!!overrides?.length && (
        <div className="fs-card pad st-pcard">
          <h3 className="br-h">Forms setting their own</h3>
          <p className="br-sub">
            These do not use the branding above — each one keeps its own answer to the things it
            lists. Resetting a form drops those answers so it follows the account again; its header
            text and width are its own and stay.
          </p>
          <div className="br-overrides">
            {overrides.map((f) => (
              <label className="br-override" key={f.id}>
                <input
                  type="checkbox" checked={picked.has(f.id)}
                  onChange={(e) => setPicked((cur) => {
                    const next = new Set(cur);
                    if (e.target.checked) next.add(f.id); else next.delete(f.id);
                    return next;
                  })}
                />
                <span className="what">
                  {f.name}
                  {f.status !== 'published' && <span className="fs-pill neutral">{f.status}</span>}
                </span>
                <span className="keys">{f.overrides.map(keyLabel).join(', ')}</span>
              </label>
            ))}
          </div>
          <div className="br-override-acts">
            <button className="fs-btn neutral sm" type="button" disabled={resetting || !picked.size}
              onClick={onResetPicked}>
              {resetting
                ? 'Resetting…'
                : `Reset ${picked.size} form${picked.size === 1 ? '' : 's'} to the account branding`}
            </button>
            <button className="fs-btn neutral sm" type="button" disabled={resetting}
              onClick={() => setPicked((cur) => (cur.size === overrides.length ? new Set() : new Set(overrides.map((f) => f.id))))}>
              {picked.size === overrides.length ? 'Select none' : 'Select all'}
            </button>
          </div>
          <div className="br-hint">
            Published forms change for respondents straight away. This cannot be undone except by
            setting each form&rsquo;s look again.
          </div>
        </div>
      )}
      {overrides?.length === 0 && state.formCount > 0 && (
        <div className="fs-card pad st-pcard">
          <h3 className="br-h">Forms setting their own</h3>
          <p className="br-sub" style={{ margin: 0 }}>
            None — every form uses this branding for everything it does not set itself.
          </p>
        </div>
      )}

      <SaveBar>
        <button className="fs-btn primary" type="button" disabled={saving || !dirty} onClick={onSaveClick}>
          {saving ? 'Saving…' : 'Save branding'}
        </button>
        <span className="note">
          {dirty
            ? `Unsaved${state.inheritingCount > 0 ? ` — will change ${state.inheritingCount} of ${state.formCount} form${state.formCount === 1 ? '' : 's'}` : ''}.`
            : 'Saved. Logos apply the moment you add or remove one.'}
        </span>
      </SaveBar>

      {confirming && (
        <Modal
          narrow
          title="Apply to live forms?"
          onClose={() => setConfirming(false)}
          footer={
            <>
              <button className="fs-btn primary" type="button" disabled={saving}
                onClick={() => void save().catch(() => undefined)}>
                {saving ? 'Applying…' : `Apply to ${state.inheritingCount} form${state.inheritingCount === 1 ? '' : 's'}`}
              </button>
              <span style={{ flex: 1 }} />
              <button className="fs-btn neutral" type="button" disabled={saving}
                onClick={() => setConfirming(false)}>Cancel</button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {state.inheritingCount} of your {state.formCount} form{state.formCount === 1 ? '' : 's'} use
            {state.inheritingCount === 1 ? 's' : ''} something from this branding, and
            {phone ? ' ' : ' '}
            {state.inheritingCount === 1 ? 'it changes' : 'they change'} as soon as you save — there is no
            publish step for branding.
          </p>
          <p style={{ margin: '10px 0 0', lineHeight: 1.6, color: 'var(--text-muted)' }}>
            A form that sets its own colour, logo or typeface keeps it.
          </p>
        </Modal>
      )}
      {guard.node}
    </div>
  );
}
